/**
 * Payroll API layer — wired to real backend endpoints.
 *
 * Response normalizers translate the real API field names to the shape
 * the frontend pages expect, so no page components need to change.
 */

import { authData } from "./Service";
import {
  PAYROLL_TEMPLATE_LIST,
  PAYROLL_TEMPLATE_CREATE,
  PAYROLL_TEMPLATE_VIEW,
  PAYROLL_TEMPLATE_EDIT,
  PAYROLL_TEMPLATE_CLONE,
  PAYROLL_TEMPLATE_TOGGLE,
  PAYROLL_TEMPLATE_DELETE,
  PAYROLL_RUN_INITIATE,
  PAYROLL_RUN_VIEW,
  PAYROLL_RUN_APPROVE,
  PAYROLL_RUN_MARK_PAID,
  PAYROLL_BATCH_LIST,
  PAYROLL_SALARY_REGISTER,
  PAYROLL_PF_REPORT,
  PAYROLL_TDS_REPORT,
  PAYROLL_BANK_TRANSFER,
  PT_SLAB_LIST,
  PT_SLAB_SAVE,
  TDS_SLAB_LIST,
  TDS_SLAB_SAVE,
} from "./apiRoutes";
import { getLocalItem } from "../Constant/common";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve organizationId from argument or localStorage */
const resolveOrgId = (orgId) =>
  orgId ?? (getLocalItem("organizationId") ? Number(getLocalItem("organizationId")) : undefined);

/**
 * Normalize a single component from real API shape → frontend shape.
 * Real:  { id, component_name, component_type, calculation_method, value,
 *           base_component_name, is_taxable, sort_order, is_active }
 * Front: { id, name, calculationType, fixedAmount, percentage,
 *           baseComponentId, isTaxable, order }
 */
const normalizeComponent = (c) => ({
  id: c.id ?? c.component_name,
  name: c.component_name ?? c.name ?? "",
  component_type: c.component_type ?? "Earning",
  calculationType:
    c.calculation_method === "Fixed"
      ? "fixed"
      : c.calculation_method === "Percentage of Basic"
        ? "percentage_of_component"
        : c.calculation_method === "Percentage of CTC"
          ? "percentage_of_gross"
          : c.calculation_method === "Percentage of Gross"
            ? "percentage_of_gross"
            : c.calculation_method === "Residual"
              ? "residual"
              : (c.calculationType ?? "fixed"),
  fixedAmount: c.calculation_method === "Fixed" ? Number(c.value ?? 0) : (c.fixedAmount ?? 0),
  percentage: c.calculation_method !== "Fixed" ? Number(c.value ?? 0) : (c.percentage ?? 0),
  baseComponentId: c.base_component_name ?? c.baseComponentId ?? null,
  isTaxable: c.is_taxable ?? c.isTaxable ?? false,
  isProrata: c.is_prorata ?? c.isProrata ?? true, // default true
  isResidual: !!(c.is_residual ?? c.isResidual ?? c.calculation_method === "Residual"),
  order: c.sort_order ?? c.order ?? 0,
  is_active: c.is_active ?? true,
});

/**
 * Normalize a single template from real API shape → frontend shape.
 * Real:  { id, template_name, description, is_active, deduction_config, components[] }
 * Front: { id, name, status, description, earningComponents[], deductionComponents{}, roles[] }
 */
const normalizeTemplate = (t) => {
  // deduction_config may arrive as a JSON string — parse it
  let rawDc = t.deduction_config ?? t.deductionComponents ?? {};
  if (typeof rawDc === "string") {
    try {
      rawDc = JSON.parse(rawDc);
    } catch {
      rawDc = {};
    }
  }
  const dc = rawDc;
  return {
    id: t.id,
    name: t.template_name ?? t.name ?? "",
    description: t.description ?? "",
    status:
      t.is_active === false
        ? "Inactive"
        : t.is_active === true
          ? "Active"
          : (t.status ?? "Inactive"),
    roles: t.roles ?? [],
    assignedRoles: t.assignedRoles ?? [],
    earningComponents: (() => {
      const raw = (t.components ?? t.earningComponents ?? [])
        .filter((c) => (c.component_type ?? "Earning") === "Earning")
        .map(normalizeComponent);
      return raw.map((c) => {
        if (c.baseComponentId) {
          const match = raw.find(
            (other) =>
              other.id !== c.id &&
              (other.name === c.baseComponentId || String(other.id) === String(c.baseComponentId)),
          );
          if (match) {
            return { ...c, baseComponentId: match.id };
          }
        }
        return c;
      });
    })(),
    deductionComponents: {
      pf: {
        enabled: dc.pf?.enabled ?? false,
        employeePercent: dc.pf?.employeePercent ?? dc.pf?.employee_percent ?? 12,
        employerPercent: dc.pf?.employerPercent ?? dc.pf?.employer_percent ?? 12,
      },
      esic: {
        enabled: dc.esic?.enabled ?? false,
        employeePercent: dc.esic?.employeePercent ?? dc.esic?.employee_percent ?? 0.75,
        eligibilityCeiling: dc.esic?.eligibilityCeiling ?? dc.esic?.eligibility_ceiling ?? 21000,
      },
      pt: { enabled: dc.pt?.enabled ?? false },
      tds: { enabled: dc.tds?.enabled ?? false },
      custom: dc.custom ?? [],
    },
    createdAt: t.createdAt ?? t.created_at,
    updatedAt: t.updatedAt ?? t.updated_at,
  };
};

/**
 * Normalize a single employee salary record from real API → frontend shape.
 * Real:  { employee_name, employee_code, gross_salary, net_salary, pf_employee,
 *           esic_amount, pt_amount, tds_amount, lop_amount, component_breakdown[] }
 * Front: { employeeName, employeeId, grossSalary, netSalary, pfEmployee,
 *           esic, pt, tds, lopAmount, earnings[], deductions[] }
 */

/**
 * Normalize a batch summary from real API → frontend shape.
 * Real:  { id, month (string), month_number, year, status,
 *           total_employees, total_gross_salary, total_net_salary, total_deductions }
 * Front: { id, month, year, payrollMonth, status, totalEmployees,
 *           totalGross, totalNet, totalDeductions, summary{} }
 */
const normalizeBatch = (b) => ({
  id: b.id,
  month: b.month_number ?? b.month,
  year: b.year,
  payrollMonth:
    typeof b.month === "string" && isNaN(Number(b.month))
      ? b.month
      : (b.payrollMonth ?? `${b.month}/${b.year}`),
  status: b.status ?? "Draft",
  totalEmployees: b.total_employees ?? b.totalEmployees ?? 0,
  totalGross: b.total_gross_salary ?? b.totalGross ?? 0,
  totalDeductions: b.total_deductions ?? b.totalDeductions ?? 0,
  totalNet: b.total_net_salary ?? b.totalNet ?? 0,
  approvedBy: b.approved_by ?? b.approvedBy ?? null,
  approvedAt: b.approved_at ?? b.approvedAt ?? null,
  paidAt: b.paid_at ?? b.paidAt ?? null,
  remarks: b.remarks ?? "",
  summary: {
    totalEmployees: b.total_employees ?? b.totalEmployees ?? 0,
    totalGross: b.total_gross_salary ?? b.totalGross ?? 0,
    totalDeductions: b.total_deductions ?? b.totalDeductions ?? 0,
    totalNet: b.total_net_salary ?? b.totalNet ?? 0,
  },
});

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

// Maps frontend calculationType -> backend calculation_method enum
// TODO: update "Residual" mapping once backend adds the enum value
const toCalcMethod = (ct) =>
  ct === "fixed"
    ? "Fixed"
    : ct === "percentage" || ct === "percentage_of_component"
      ? "Percentage of Basic"
      : ct === "percentage_of_gross" || ct === "percentage_of_ctc"
        ? "Percentage of CTC"
        : ct === "residual"
          ? "Percentage of CTC"
          : "Fixed";

export const listTemplatesApi = (orgId) => {
  const id = resolveOrgId(orgId);
  return authData.get(`${PAYROLL_TEMPLATE_LIST}${id ? `?organizationId=${id}` : ""}`).then((r) => {
    const resp = r.data;
    if (resp?.data && Array.isArray(resp.data)) {
      return { ...resp, data: resp.data.map(normalizeTemplate) };
    }
    return resp;
  });
};

export const createTemplateApi = (data) => {
  const orgId = resolveOrgId(data.organization_id);
  // Transform frontend shape → real API shape
  const allComponents = [
    ...(data.earningComponents ?? []).map((c, i) => ({
      component_name: c.name,
      component_type: "Earning",
      calculation_method: toCalcMethod(c.calculationType),
      value: c.calculationType === "fixed" ? Number(c.fixedAmount ?? 0) : Number(c.percentage ?? 0),
      base_component_name: c.baseComponentId
        ? (data.earningComponents.find(
            (ec) => String(ec.id) === String(c.baseComponentId) || ec.name === c.baseComponentId,
          )?.name ?? (typeof c.baseComponentId === "string" ? c.baseComponentId : null))
        : null,
      is_taxable: c.isTaxable ?? false,
      is_prorata: c.isProrata !== false, // default true
      is_residual: !!(c.isResidual || c.calculationType === "residual"),
      sort_order: c.order ?? i,
    })),
  ];
  const dc = data.deductionComponents ?? {};
  const payload = {
    organization_id: orgId,
    template_name: data.name,
    description: data.description ?? "",
    components: allComponents,
    deduction_config: {
      pf: {
        enabled: dc.pf?.enabled ?? false,
        employee_percent: dc.pf?.employeePercent ?? dc.pf?.employee_percent ?? 12,
        employer_percent: dc.pf?.employerPercent ?? dc.pf?.employer_percent ?? 12,
      },
      esic: {
        enabled: dc.esic?.enabled ?? false,
        employee_percent: dc.esic?.employeePercent ?? dc.esic?.employee_percent ?? 0.75,
        employer_percent: dc.esic?.employerPercent ?? dc.esic?.employer_percent ?? 3.25,
        eligibility_ceiling: dc.esic?.eligibilityCeiling ?? dc.esic?.eligibility_ceiling ?? 21000,
      },
      pt: { enabled: dc.pt?.enabled ?? false },
      tds: { enabled: dc.tds?.enabled ?? false },
    },
  };
  return authData.post(PAYROLL_TEMPLATE_CREATE, payload).then((r) => {
    const resp = r.data;
    // API returns data as the new template ID (number) — normalise to { id }
    if (resp?.data != null && typeof resp.data !== "object") {
      return { ...resp, data: { id: resp.data } };
    }
    return resp;
  });
};

export const viewTemplateApi = (id) =>
  authData.get(`${PAYROLL_TEMPLATE_VIEW}${id}`).then((r) => {
    const resp = r.data;
    if (resp?.data) return { ...resp, data: normalizeTemplate(resp.data) };
    return resp;
  });

export const editTemplateApi = (id, data) => {
  const allComponents = [
    ...(data.earningComponents ?? []).map((c, i) => ({
      ...(c.id && !String(c.id).includes("-") ? { id: c.id } : {}),
      component_name: c.name,
      component_type: "Earning",
      calculation_method: toCalcMethod(c.calculationType),
      value: c.calculationType === "fixed" ? Number(c.fixedAmount ?? 0) : Number(c.percentage ?? 0),
      base_component_name: c.baseComponentId
        ? (data.earningComponents.find(
            (ec) => String(ec.id) === String(c.baseComponentId) || ec.name === c.baseComponentId,
          )?.name ?? (typeof c.baseComponentId === "string" ? c.baseComponentId : null))
        : null,
      is_taxable: c.isTaxable ?? false,
      is_prorata: c.isProrata !== false, // default true
      is_residual: !!(c.isResidual || c.calculationType === "residual"),
      sort_order: c.order ?? i,
      is_active: true,
    })),
  ];
  const dc = data.deductionComponents ?? {};
  const payload = {
    template_name: data.name,
    description: data.description ?? "",
    components: allComponents,
    deduction_config: {
      pf: {
        enabled: dc.pf?.enabled ?? false,
        employee_percent: dc.pf?.employeePercent ?? dc.pf?.employee_percent ?? 12,
        employer_percent: dc.pf?.employerPercent ?? dc.pf?.employer_percent ?? 12,
      },
      esic: {
        enabled: dc.esic?.enabled ?? false,
        employee_percent: dc.esic?.employeePercent ?? dc.esic?.employee_percent ?? 0.75,
        employer_percent: dc.esic?.employerPercent ?? dc.esic?.employer_percent ?? 3.25,
        eligibility_ceiling: dc.esic?.eligibilityCeiling ?? dc.esic?.eligibility_ceiling ?? 21000,
      },
      pt: { enabled: dc.pt?.enabled ?? false },
      tds: { enabled: dc.tds?.enabled ?? false },
    },
  };
  return authData.put(`${PAYROLL_TEMPLATE_EDIT}${id}`, payload).then((r) => {
    const resp = r.data;
    // API returns { id, usedInApprovedBatch } — ensure data.id is present
    if (resp?.data != null && typeof resp.data !== "object") {
      return { ...resp, data: { id: resp.data } };
    }
    return resp;
  });
};

export const cloneTemplateApi = (id) =>
  authData.post(`${PAYROLL_TEMPLATE_CLONE}${id}`).then((r) => r.data);

export const toggleTemplateApi = (id) =>
  authData.patch(`${PAYROLL_TEMPLATE_TOGGLE}${id}`).then((r) => r.data);

export const deleteTemplateApi = (id) =>
  authData.delete(`${PAYROLL_TEMPLATE_DELETE}${id}`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Payroll Run
// ---------------------------------------------------------------------------

export const initiateRunApi = (data) => {
  const orgId = resolveOrgId(data.organization_id);
  return authData
    .post(PAYROLL_RUN_INITIATE, { ...data, organization_id: orgId })
    .then((r) => r.data);
};

export const viewBatchApi = (id) =>
  authData.get(`${PAYROLL_RUN_VIEW}${id}`).then((r) => {
    const resp = r.data;
    if (!resp?.data) return resp;
    const raw = resp.data;

    // Real API returns employee records under "details" key
    const rawEmployees = raw.details ?? raw.salaries ?? raw.employees ?? [];

    const employees = rawEmployees.map((e) => {
      // component_breakdown may be a JSON string — parse it
      let breakdown = e.component_breakdown ?? [];
      if (typeof breakdown === "string") {
        try {
          breakdown = JSON.parse(breakdown);
        } catch {
          breakdown = [];
        }
      }

      // Normalize component_breakdown → earnings[] + deductions[]
      const earnings = breakdown
        .filter((c) => (c.component_type ?? c.type ?? "Earning") === "Earning")
        .map((c) => ({ name: c.component_name ?? c.name, amount: Number(c.amount) || 0 }));

      const deductions = breakdown
        .filter((c) => (c.component_type ?? c.type ?? "") === "Deduction")
        .filter((c) => {
          const compName = (c.component_name ?? c.name ?? "").toLowerCase();
          return !compName.includes("employer") && !c.is_employer && !c.isEmployer;
        })
        .map((c) => ({ name: c.component_name ?? c.name, amount: Number(c.amount) || 0 }));

      // employee_name may be empty string — fall back to staff.name
      const employeeName =
        e.employee_name && e.employee_name.trim()
          ? e.employee_name
          : (e.staff?.name ?? e.staff?.first_name ?? "");

      return {
        id: e.id,
        staffId: e.staff_id ?? e.staffId,
        employeeName,
        employeeId: e.employee_code ?? e.employeeId ?? "",
        designation: e.designation ?? e.staff?.designation ?? "",
        department: e.department ?? "",
        panNumber: e.pan_number ?? e.panNumber ?? "",
        uanNumber: e.uan_number ?? e.uanNumber ?? "",
        bankAccount: e.bank_account ?? e.bankAccount ?? "",
        ifscCode: e.ifsc_code ?? e.ifscCode ?? "",
        workingDays: Number(e.total_working_days ?? e.workingDays ?? 0),
        presentDays: Number(e.present_days ?? e.presentDays ?? 0),
        absentDays: Number(e.absent_days ?? e.absentDays ?? 0),
        paidLeaveDays: Number(e.paid_leave_days ?? e.paidLeaveDays ?? 0),
        overtimeHours: (() => {
          const mins = e.overtime_minutes ?? e.overtimeMinutes ?? e.ot_minutes;
          if (mins != null && !isNaN(Number(mins))) {
            return parseFloat((Number(mins) / 60).toFixed(2));
          }
          const hrs = e.overtimeHours ?? e.overtime_hours ?? e.ot_hours;
          if (hrs != null && !isNaN(Number(hrs))) {
            return Number(hrs);
          }
          return 0;
        })(),
        overtimeMinutes: (() => {
          const mins = e.overtime_minutes ?? e.overtimeMinutes ?? e.ot_minutes;
          if (mins != null && !isNaN(Number(mins))) {
            return Number(mins);
          }
          const hrs = e.overtimeHours ?? e.overtime_hours ?? e.ot_hours;
          if (hrs != null && !isNaN(Number(hrs))) {
            return Number(hrs) * 60;
          }
          return 0;
        })(),
        grossSalary: Number(e.gross_salary ?? e.grossSalary ?? 0),
        lopAmount: Number(e.lop_amount ?? e.lopAmount ?? 0),
        pfEmployee: Number(e.pf_employee ?? e.pfEmployee ?? 0),
        pfEmployer: Number(e.pf_employer ?? e.pfEmployer ?? 0),
        esic: Number(e.esic_amount ?? e.esic ?? 0),
        pt: Number(e.pt_amount ?? e.pt ?? 0),
        tds: Number(e.tds_amount ?? e.tds ?? 0),
        netSalary: Number(e.net_salary ?? e.netSalary ?? 0),
        variable_amount: Number(e.variable_amount ?? e.variableAmount ?? 0),
        earnings,
        deductions,
        hasMissingAttendance: e.has_missing_attendance ?? e.hasMissingAttendance ?? false,
        ptWarning: e.pt_warning ?? e.ptWarning ?? false,
        status: e.status ?? "Draft",
        payrollMonth: raw.month ?? "",
        companyName: e.companyName ?? "",
        companyAddress: e.companyAddress ?? "",
      };
    });

    return {
      ...resp,
      data: {
        ...normalizeBatch(raw),
        employees,
        attendance: employees,
        salaries: employees,
      },
    };
  });

export const approveBatchApi = (id) =>
  authData.post(`${PAYROLL_RUN_APPROVE}${id}`).then((r) => r.data);

export const markPaidApi = (id) =>
  authData.post(`${PAYROLL_RUN_MARK_PAID}${id}`).then((r) => r.data);

export const listBatchesApi = (params) => {
  const orgId = resolveOrgId(params?.organization_id);
  return authData.post(PAYROLL_BATCH_LIST, { ...params, organization_id: orgId }).then((r) => {
    const resp = r.data;
    // API returns { data: { data: [...], totalRecordsCount, ... } }
    // Unwrap to flat array so all consumers can do Array.isArray(resp.data)
    const list = Array.isArray(resp?.data?.data)
      ? resp.data.data
      : Array.isArray(resp?.data)
        ? resp.data
        : [];
    const normalized = list.map(normalizeBatch);
    return { ...resp, data: normalized };
  });
};

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const salaryRegisterApi = (params) => {
  const orgId = resolveOrgId(params?.organization_id);
  return authData
    .post(PAYROLL_SALARY_REGISTER, {
      organization_id: orgId,
      payroll_run_id: +params?.batchId ?? +params?.payroll_run_id,
    })
    .then((r) => {
      const resp = r.data;
      const list = Array.isArray(resp?.data) ? resp.data : [];
      return {
        ...resp,
        data: list.map((e) => ({
          employeeName: e.employeeName ?? e.employee_name ?? "",
          employeeId: e.employeeId ?? e.employee_code ?? "",
          designation: e.designation ?? "",
          grossSalary: e.grossSalary ?? e.gross_salary ?? 0,
          lopAmount: e.lopAmount ?? e.lop_amount ?? 0,
          pfEmployee: e.pfEmployee ?? e.pf_employee ?? 0,
          esic: e.esic ?? e.esic_amount ?? 0,
          pt: e.pt ?? e.pt_amount ?? 0,
          tds: e.tds ?? e.tds_amount ?? 0,
          totalDeductions: e.totalDeductions ?? e.total_deductions ?? 0,
          netSalary: e.netSalary ?? e.net_salary ?? 0,
          componentBreakdown: e.componentBreakdown ?? e.component_breakdown ?? [],
        })),
      };
    });
};

export const pfReportApi = (params) => {
  const orgId = resolveOrgId(params?.organization_id);
  return authData
    .post(PAYROLL_PF_REPORT, {
      organization_id: orgId,
      payroll_run_id: +params?.batchId ?? +params?.payroll_run_id,
    })
    .then((r) => {
      const resp = r.data;
      const list = Array.isArray(resp?.data) ? resp.data : [];
      return {
        ...resp,
        data: list.map((e) => ({
          employeeName: e.employeeName ?? e.employee_name ?? "",
          uanNumber: e.uanNumber ?? e.uan_number ?? "",
          basicSalary: e.basicSalary ?? e.basic_salary ?? 0,
          employeePf: e.employeePf ?? e.employee_pf ?? e.pf_employee ?? 0,
          employerPf: e.employerPf ?? e.employer_pf ?? e.pf_employer ?? 0,
        })),
      };
    });
};

export const tdsReportApi = (params) => {
  const orgId = resolveOrgId(params?.organization_id);
  return authData
    .post(PAYROLL_TDS_REPORT, {
      organization_id: orgId,
      payroll_run_id: +params?.batchId ?? +params?.payroll_run_id,
    })
    .then((r) => {
      const resp = r.data;
      const list = Array.isArray(resp?.data) ? resp.data : [];
      return {
        ...resp,
        data: list.map((e) => ({
          employeeName: e.employeeName ?? e.employee_name ?? "",
          panNumber: e.panNumber ?? e.pan_number ?? "",
          annualIncome: e.annualIncome ?? e.annual_income ?? 0,
          annualTax: e.annualTax ?? e.annual_tax ?? 0,
          monthlyTds: e.monthlyTds ?? e.monthly_tds ?? 0,
        })),
      };
    });
};

export const bankTransferApi = (params) => {
  const orgId = resolveOrgId(params?.organization_id);
  return authData
    .post(PAYROLL_BANK_TRANSFER, {
      organization_id: orgId,
      payroll_run_id: +params?.batchId ?? +params?.payroll_run_id,
    })
    .then((r) => {
      const resp = r.data;
      const list = Array.isArray(resp?.data) ? resp.data : [];
      return {
        ...resp,
        data: list.map((e) => ({
          employeeName: e.employeeName ?? e.employee_name ?? "",
          bankAccount: e.bankAccount ?? e.bank_account ?? "",
          ifscCode: e.ifscCode ?? e.ifsc_code ?? "",
          netSalary: e.netSalary ?? e.net_salary ?? 0,
        })),
      };
    });
};

// ---------------------------------------------------------------------------
// Settings — PT Slabs
// ---------------------------------------------------------------------------

export const listPtSlabsApi = (orgId) => {
  const id = resolveOrgId(orgId);
  return authData.get(`${PT_SLAB_LIST}${id ? `?organizationId=${id}` : ""}`).then((r) => r.data);
};

export const savePtSlabsApi = (data) => {
  // data may be an array (legacy) or { organization_id, slabs }
  const orgId = resolveOrgId(Array.isArray(data) ? undefined : data?.organization_id);
  const slabs = Array.isArray(data) ? data : (data?.slabs ?? data);
  return authData.post(PT_SLAB_SAVE, { organization_id: orgId, slabs }).then((r) => r.data);
};

// ---------------------------------------------------------------------------
// Settings — TDS Slabs
// ---------------------------------------------------------------------------

export const listTdsSlabsApi = (orgId) => {
  const id = resolveOrgId(orgId);
  return authData.get(`${TDS_SLAB_LIST}${id ? `?organizationId=${id}` : ""}`).then((r) => {
    const resp = r.data;
    const list = Array.isArray(resp?.data) ? resp.data : [];
    // Normalize real API field names → frontend field names
    return {
      ...resp,
      data: list.map((s) => ({
        id: s.id,
        incomeFrom: s.income_from ?? s.incomeFrom ?? 0,
        incomeTo: s.income_to ?? s.incomeTo ?? null,
        taxRatePercent: s.tax_rate_percent ?? s.taxRatePercent ?? 0,
      })),
    };
  });
};

export const saveTdsSlabsApi = (data) => {
  const orgId = resolveOrgId(Array.isArray(data) ? undefined : data?.organization_id);
  const rawSlabs = Array.isArray(data) ? data : (data?.slabs ?? data);
  // Normalize frontend field names → real API field names
  const slabs = rawSlabs.map((s) => ({
    income_from: s.income_from ?? s.incomeFrom ?? 0,
    income_to: s.income_to ?? s.incomeTo ?? null,
    tax_rate_percent: s.tax_rate_percent ?? s.taxRatePercent ?? 0,
  }));
  // Backend requires the last slab to have income_to: null (no upper bound)
  if (slabs.length > 0) {
    slabs[slabs.length - 1].income_to = null;
  }
  return authData.post(TDS_SLAB_SAVE, { organization_id: orgId, slabs }).then((r) => r.data);
};
